import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Trash2, FileText, Share2, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useExportToPDF } from "@/hooks/useExportToPDF";
import { PrescriptionReceitaPreview } from "./PrescriptionReceitaPreview";
import { NotificationPlanner } from "./NotificationPlanner";
import { SharePrescriptionModal } from "./SharePrescriptionModal";
import { PrescriptionPDFContent } from "./PrescriptionPDFContent";

interface PrescriptionFinalizationStepProps {
  prescriptionId: string;
  patient: {
    id: string;
    full_name: string;
    data_nascimento?: string;
    cpf?: string;
    email?: string;
    celular?: string;
  };
  prescriptionItems: Array<{
    id: string;
    prescription: string;
    dosage?: string;
    duration?: string;
    item_type?: string;
    frequency?: string;
    usage_route?: string;
  }>;
  professionalName: string;
  professionalCpfCrmCrn?: string;
  prescriptionDate?: string;
}

export function PrescriptionFinalizationStep({
  prescriptionId,
  patient,
  prescriptionItems,
  professionalName,
  professionalCpfCrmCrn,
  prescriptionDate,
}: PrescriptionFinalizationStepProps) {
  const navigate = useNavigate();
  const pdfRef = useRef<HTMLDivElement>(null);
  const { exportToPDF, isExporting } = useExportToPDF(pdfRef);
  
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const handleDiscard = async () => {
    try {
      if (!prescriptionId) {
        navigate("/app/aplicativo/prescription");
        return;
      }

      const { error } = await supabase
        .from("prescriptions")
        .delete()
        .eq("id", prescriptionId);

      if (error) throw error;

      toast.success("Prescrição descartada");
      navigate("/app/aplicativo/prescription");
    } catch (error) {
      console.error("Erro ao descartar prescrição:", error);
      toast.error("Erro ao descartar prescrição");
    }
  };

  const handleExportPDF = () => {
    exportToPDF(`receita-${patient.full_name}-${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const handleSaveNotifications = async (config: {
    notifyEmail: boolean;
    notifyWhatsApp: boolean;
    notifySMS: boolean;
    itemsConfig: Record<string, any>;
  }) => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Save notifications to database
      const notificationsToInsert = Object.entries(config.itemsConfig).map(
        ([itemName, itemConfig]: [string, any]) => {
          const item = prescriptionItems.find((i) => i.prescription === itemName);
          
          // Convert weekdays to schedule_days format
          const scheduleDays = itemConfig.weekdays;
          
          // Calculate end_date based on duration
          const startDate = new Date();
          const durationDays = parseInt(itemConfig.durationDays) || 30;
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + durationDays);

          return {
            prescription_id: prescriptionId || null,
            prescription_item_id: item?.id || null,
            patient_id: patient.id,
            professional_id: user.id,
            description: itemName,
            dose_time: itemConfig.time || "08:00",
            frequency: `${itemConfig.frequency}_${itemConfig.period}`,
            days_of_week: scheduleDays,
            start_date: startDate.toISOString().split("T")[0],
            end_date: itemConfig.durationDays === "continuous" 
              ? null 
              : endDate.toISOString().split("T")[0],
            notify_email: config.notifyEmail,
            notify_whatsapp: config.notifyWhatsApp,
            notify_sms: config.notifySMS,
            status: "active",
          };
        }
      );

      const { error } = await supabase
        .from("patient_medication_plans")
        .insert(notificationsToInsert);

      if (error) throw error;

      console.log("Notificações salvas com sucesso:", notificationsToInsert);
    } catch (error) {
      console.error("Erro ao salvar notificações:", error);
      throw error;
    }
  };

  const handleFinalize = async () => {
    setIsCompleting(true);
    try {
      const { error } = await supabase
        .from("prescriptions")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", prescriptionId);

      if (error) throw error;

      toast.success("Prescrição finalizada com sucesso!");
      navigate("/app/aplicativo/prescription");
    } catch (error) {
      console.error("Erro ao finalizar prescrição:", error);
      toast.error("Erro ao finalizar prescrição");
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Hidden PDF Content for Export */}
      <div className="hidden">
        <div ref={pdfRef}>
          <PrescriptionPDFContent
            prescription={{
              id: prescriptionId,
              patient: {
                full_name: patient.full_name,
                data_nascimento: patient.data_nascimento,
                cpf: patient.cpf,
              },
              professional: {
                full_name: professionalName,
                cpf: professionalCpfCrmCrn,
              },
              items: prescriptionItems.map((item) => ({
                item_type: item.item_type || "medication",
                prescription: item.prescription,
                dosage: item.dosage,
                frequency: item.frequency,
                duration: item.duration,
              })),
              created_at: prescriptionDate || new Date().toISOString(),
            }}
          />
        </div>
      </div>

      {/* Section 1: Receita Criada */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          📋 Receita Criada
        </h2>
        <PrescriptionReceitaPreview
          patient={patient}
          prescriptionItems={prescriptionItems}
          prescriptionDate={prescriptionDate}
        />
      </div>

      {/* Section 2: Planejar Administração */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          🔔 Planejar Administração do Paciente
        </h2>
        <NotificationPlanner
          prescriptionItems={prescriptionItems}
          onSave={handleSaveNotifications}
        />
      </div>

      {/* Section 3: Rodapé com Ações - Layout 100% Vertical no Mobile */}
      <div className="pt-6 border-t border-border">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Botão Finalizar - primeiro no mobile, último no desktop */}
          <Button
            onClick={handleFinalize}
            disabled={isCompleting}
            className="w-full sm:w-auto order-first sm:order-last sm:ml-auto bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Check className="h-4 w-4 mr-2" />
            {isCompleting ? "Finalizando..." : "Finalizar Prescrição"}
          </Button>
          
          {/* Botão Gerar PDF */}
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={isExporting}
            className="w-full sm:w-auto border-border text-foreground hover:bg-muted"
          >
            <FileText className="h-4 w-4 mr-2" />
            {isExporting ? "Gerando..." : "Gerar PDF"}
          </Button>
          
          {/* Botão Compartilhar */}
          <Button
            variant="outline"
            onClick={() => setShowShareModal(true)}
            className="w-full sm:w-auto border-border text-foreground hover:bg-muted"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Compartilhar
          </Button>
          
          {/* Botão Descartar - último no mobile, primeiro no desktop */}
          {prescriptionId && (
            <Button
              variant="outline"
              onClick={() => setShowDiscardDialog(true)}
              className="w-full sm:w-auto order-last sm:order-first border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Descartar Prescrição
            </Button>
          )}
        </div>
      </div>

      {/* Discard Confirmation Dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Descartar Prescrição?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Esta ação não pode ser desfeita. Todos os dados desta prescrição serão
              permanentemente removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-foreground hover:bg-muted">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Modal */}
      <SharePrescriptionModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        patient={patient}
        prescriptionItems={prescriptionItems}
        professionalName={professionalName}
        professionalCpfCrmCrn={professionalCpfCrmCrn}
        patientId={patient.id}
        onDownloadPDF={handleExportPDF}
      />
    </div>
  );
}
